import { Link } from '@tanstack/react-router';
import { Building2, BriefcaseBusiness, LayoutDashboard } from 'lucide-react';

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@shigo-ui/ui';

import type { publisherDashboardPath } from '../../features/auth/dashboardDestination';
import { dashboardT } from '../../lib/messages';

interface DashboardOption {
  id: string;
  name: string;
}

export interface ShigoAccountDashboardSwitcherProps {
  showMainDashboard: boolean;
  /** Publisher dashboards other than the active one. */
  publishers: DashboardOption[];
  /** Agency workspaces other than the active one. */
  agencies: DashboardOption[];
  publisherLandingPath: ReturnType<typeof publisherDashboardPath>;
}

/** Account-menu items that switch between the main, publisher, and agency dashboards. */
export function ShigoAccountDashboardSwitcher({
  showMainDashboard,
  publishers,
  agencies,
  publisherLandingPath,
}: ShigoAccountDashboardSwitcherProps) {
  const hasPublishers = publishers.length > 0;
  const hasAgencies = agencies.length > 0;
  return (
    <>
      <DropdownMenuSeparator />
      {showMainDashboard ? (
        <>
          <DropdownMenuItem asChild className="cursor-pointer">
            <Link to="/">
              <LayoutDashboard className="h-4 w-4" />
              {dashboardT('dashboardSelection.main')}
            </Link>
          </DropdownMenuItem>
          {hasPublishers || hasAgencies ? <DropdownMenuSeparator /> : null}
        </>
      ) : null}
      {hasPublishers ? (
        <DropdownMenuGroup aria-label={dashboardT('dashboardSelection.publisherTitle')}>
          <DropdownMenuLabel className="text-muted-foreground flex items-center gap-2 py-2 text-xs font-medium">
            <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
            {dashboardT('dashboardSelection.publisherTitle')}
          </DropdownMenuLabel>
          {publishers.map((publisher) => (
            <DropdownMenuItem asChild className="cursor-pointer" key={publisher.id}>
              <Link to={publisherLandingPath} params={{ publisherId: publisher.id }}>
                <span className="truncate" title={publisher.name}>
                  {publisher.name}
                </span>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      ) : null}
      {hasAgencies ? (
        <DropdownMenuGroup aria-label={dashboardT('dashboardSelection.agencyTitle')}>
          {hasPublishers ? <DropdownMenuSeparator /> : null}
          <DropdownMenuLabel className="text-muted-foreground flex items-center gap-2 py-2 text-xs font-medium">
            <BriefcaseBusiness className="h-3.5 w-3.5" aria-hidden="true" />
            {dashboardT('dashboardSelection.agencyTitle')}
          </DropdownMenuLabel>
          {agencies.map((agency) => (
            <DropdownMenuItem asChild className="cursor-pointer" key={agency.id}>
              <Link to="/a/$agencyId" params={{ agencyId: agency.id }}>
                <span className="truncate" title={agency.name}>
                  {agency.name}
                </span>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      ) : null}
    </>
  );
}
